import React, { createContext, useContext, useState, useCallback } from "react";

const FloatingPanelContext = createContext();

// Available positions in priority order
const POSITIONS = ["top-left", "top-right", "bottom-left", "bottom-right"];

export const FloatingPanelProvider = ({ children }) => {
  const [floatingPanels, setFloatingPanels] = useState([]);

  // Find the next available position
  const getNextAvailablePosition = useCallback((currentPanels) => {
    const occupiedPositions = currentPanels.map((p) => p.position);
    for (const pos of POSITIONS) {
      if (!occupiedPositions.includes(pos)) {
        return pos;
      }
    }
    // If all quadrants are full, stack in top-left
    return "top-left";
  }, []);

  const addFloatingPanel = useCallback(
    (content, title, preferredPosition = null) => {
      const id = `floating-${Date.now()}`;

      setFloatingPanels((prev) => {
        // Determine position - use preferred if available, otherwise find next available
        let position = preferredPosition;
        if (!position || prev.some((p) => p.position === position)) {
          position = getNextAvailablePosition(prev);
        }

        const newPanel = {
          id,
          title,
          content,
          position,
        };
        return [...prev, newPanel];
      });

      return id;
    },
    [getNextAvailablePosition]
  );

  const removeFloatingPanel = useCallback((id) => {
    setFloatingPanels((prev) => prev.filter((panel) => panel.id !== id));
  }, []);

  const updatePanelPosition = useCallback((id, newPosition) => {
    setFloatingPanels((prev) => {
      // Check if position is already occupied by another panel
      const occupyingPanel = prev.find(
        (p) => p.position === newPosition && p.id !== id
      );

      if (occupyingPanel) {
        // Swap positions
        const currentPanel = prev.find((p) => p.id === id);
        const currentPosition = currentPanel?.position;

        return prev.map((panel) => {
          if (panel.id === id) {
            return { ...panel, position: newPosition };
          }
          if (panel.id === occupyingPanel.id) {
            return { ...panel, position: currentPosition };
          }
          return panel;
        });
      }

      // Just update the position
      return prev.map((panel) =>
        panel.id === id ? { ...panel, position: newPosition } : panel
      );
    });
  }, []);

  const value = {
    floatingPanels,
    addFloatingPanel,
    removeFloatingPanel,
    updatePanelPosition,
  };

  return (
    <FloatingPanelContext.Provider value={value}>
      {children}
    </FloatingPanelContext.Provider>
  );
};

export const useFloatingPanels = () => {
  const context = useContext(FloatingPanelContext);
  if (!context) {
    throw new Error(
      "useFloatingPanels must be used within a FloatingPanelProvider"
    );
  }
  return context;
};
